import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { MenuNavLink } from 'src/client/containers/HeaderBar/styles';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { HeaderContext, HeaderPopovers } from 'src/client/containers/HeaderBar/HeaderProvider';
import styled from 'styled-components/macro';
import { usePopper } from 'react-popper';
import ReactDOM from 'react-dom';
import { portalRoot } from 'src/client/helpers/portalRoot';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TABLET_LANDSCAPE } from 'src/client/constants/mediaQueries';

type ArrowStyle = 'white';

type Props = {
  arrowStyle?: ArrowStyle;
  className?: string;
  headerPopover: HeaderPopovers;
  menuLink: string;
  menuText: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  staticContent?: boolean;
};

export const HeaderItemWithPopover = (props: PropsWithChildren<Props>) => {
  const {
    arrowStyle,
    children,
    className,
    headerPopover,
    menuLink,
    menuText,
    onClick,
    // If we have static content that we don't need to wait for, just
    // force the arrow and content into place
    staticContent = false,
  } = props;
  const { activePopover, setActivePopover, popoverContentLoaded, setPopoverContentLoaded } = useContext(HeaderContext);

  const [referenceElement, setReferenceElement] = useState<HTMLAnchorElement | HTMLSpanElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
    modifiers: [{ name: 'arrow', options: { element: arrowElement } }],
    strategy: 'fixed',
  });

  useEffect(() => {
    if (popoverContentLoaded) {
      update?.();
    }

    if (staticContent && activePopover !== null) {
      setPopoverContentLoaded(true);
    }
  }, [popoverContentLoaded, update, staticContent, setPopoverContentLoaded, activePopover]);

  const menuIsOpen = activePopover === headerPopover;
  const canShowHoverPopover = () =>
    typeof window !== 'undefined' && window.matchMedia(`${TABLET_LANDSCAPE} and (hover: hover)`).matches;
  const showMenu = () => {
    if (!canShowHoverPopover()) {
      return;
    }

    setActivePopover(headerPopover);
  };
  const hideMenu = () => setActivePopover(null);
  const showArrow = menuIsOpen && (popoverContentLoaded || staticContent);

  useEffect(() => {
    const closePopoverIfViewportTooSmall = () => {
      if (!canShowHoverPopover() && activePopover === headerPopover) {
        setActivePopover(null);
      }
    };

    closePopoverIfViewportTooSmall();
    window.addEventListener('resize', closePopoverIfViewportTooSmall);

    return () => {
      window.removeEventListener('resize', closePopoverIfViewportTooSmall);
    };
  }, [activePopover, headerPopover, setActivePopover]);

  const linkProps = {
    className,
    onMouseOver: showMenu,
    onFocus: showMenu,
    onBlur: hideMenu,
    onMouseLeave: hideMenu,
    onClick: onClick,
    ref: setReferenceElement,
  };

  const mainLink = (
    <MenuNavLink exact to={menuLink} {...linkProps}>
      {menuText}
      <HoverMenuCaret icon={faCaretDown} size="lg" />
    </MenuNavLink>
  );

  return (
    <>
      {mainLink}
      {menuIsOpen &&
        ReactDOM.createPortal(
          <PopoverWrapper
            ref={setPopperElement}
            style={styles.popper}
            onMouseEnter={showMenu}
            onMouseLeave={hideMenu}
            {...attributes.popper}
          >
            {showArrow && <Arrow arrowStyle={arrowStyle} ref={setArrowElement} style={styles.arrow} />}
            {children}
          </PopoverWrapper>,
          portalRoot,
        )}
    </>
  );
};

const PopoverWrapper = styled.div`
  z-index: 9999;
`;

const Arrow = styled.div<{ arrowStyle?: ArrowStyle }>`
  visibility: hidden;

  &,
  &::before {
    position: absolute;
    width: 12px;
    height: 12px;
    top: -3px;
    background-color: ${(props) => (props.arrowStyle === 'white' ? '#fff' : props.theme.primary3)};
    z-index: -1;
  }

  &::before {
    visibility: visible;
    content: '';
    transform: rotate(45deg);
  }
`;

const HoverMenuCaret = styled(FontAwesomeIcon)`
  display: none;
  margin-left: 2.5px;

  @media ${TABLET_LANDSCAPE} and (hover: hover) {
    display: inline;
  }
`;
